import { useState } from "react";
import { z } from "zod";
import PageHero from "@/components/landing/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSEO } from "@/hooks/useSEO";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Send, Loader2, ShieldCheck } from "lucide-react";
import { TurnstileWidget, HoneypotField } from "@/components/security/TurnstileWidget";

const DEPARTMENTS = [
  { value: "business_development", label: "Business Development" },
  { value: "enterprise", label: "Enterprise" },
  { value: "operations", label: "Operations" },
  { value: "technical", label: "Technical" },
    { value: "founders", label: "Founders" },
] as const;

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  department: z.enum(["business_development", "enterprise", "operations", "technical", "founders"], {
    errorMap: () => ({ message: "Please select a department" }),
  }),
  company: z.string().trim().min(1, "Company is required").max(150),
  role: z.string().trim().min(1, "Role is required").max(100),
  sites: z.string().min(1, "Please select number of sites"),
  userRange: z.string().min(1, "Please select a user range"),
  intendedUse: z.string().trim().min(1, "Intended use is required").max(500),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000),
});

const SITES = ["1 site", "2–5 sites", "6+ sites"];
const USER_RANGES = ["Up to 25 users", "26–100 users", "101–300 users", "300+ users"];
const EMPTY = { name: "", email: "", company: "", role: "", sites: "", userRange: "", intendedUse: "", department: "", message: "" };

const Contact = () => {
  useSEO({
    title: "Pharmalify Spine | Consultation & Contract Requests",
    description:
      "Request a private Pharmalify Spine walkthrough, biopharma consultation, custom proposal or GxP validation and contract details.",
    keywords:
      "contact Pharmalify, pharmaceutical AI demo, biopharma compliance contact, enterprise pharma AI, Pharmalify sales",
    canonical: "https://www.pharmalify.com/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Pharmalify",
      url: "https://www.pharmalify.com/contact",
         description:
         "Request a Pharmalify Spine consultation, proposal or validation package details, including IQ/OQ/PQ support. Access requires a formal company contract.",
      mainEntity: {
        "@type": "Organization",
        name: "Pharmalify",
        url: "https://www.pharmalify.com",
        contactPoint: DEPARTMENTS.map((d) => ({
          "@type": "ContactPoint",
          contactType: d.label,
          email: d.value === "business_development"
            ? "business_development@pharmalify.com"
            : `${d.value}@pharmalify.com`,
          availableLanguage: ["English"],
          areaServed: "Worldwide",
        })),
      },
    },
  });
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot — silently reject bots
    if (honeypot) {
      toast({ title: "Message sent", description: "Thank you for reaching out." });
      setForm(EMPTY);
      return;
    }

    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      const firstError = Object.values(parsed.error.flatten().fieldErrors).flat()[0];
      toast({ title: "Please check the form", description: firstError, variant: "destructive" });
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Please complete the CAPTCHA",
        description: "We need to verify you're human before sending.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-message", {
        body: { ...parsed.data, captchaToken },
      });

      if (error) throw error;

      toast({
        title: "Message sent",
        description: "Thank you for reaching out. Our team will get back to you shortly.",
      });
      setForm(EMPTY);
      setCaptchaToken(null);
      // Reset Turnstile widget by remounting via key change is also possible; here we rely on auto-reset.
    } catch (err) {
      console.error("Contact form error:", err);
      toast({
        title: "Unable to send message",
        description: "Please try again in a moment, or email us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <PageHero
         title="Request Walkthrough"
         description="Request a private walkthrough, custom proposal or validation and contract details for Pharmalify Spine: Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS), with native AI Agents and Regulatory Watch."
      />

      <section className="py-16 md:py-20">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-start">
            {/* Left: Info panel */}
            <div className="space-y-6">
              <Card className="p-6 border-border/60">
                <h3 className="text-base font-semibold text-foreground mb-4 tracking-tight">
                   Talk to the right team
                </h3>
                <ul className="space-y-3.5">
                  {DEPARTMENTS.map((d) => (
                    <li key={d.value} className="flex items-start gap-3">
                      <div className="mt-0.5 w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center shrink-0">
                        <Mail className="w-3.5 h-3.5 text-accent" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground uppercase tracking-wider">
                          {d.label}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {d.value === "business_development"
                            ? "business_development@pharmalify.com"
                            : `${d.value}@pharmalify.com`}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>

              <div className="flex items-start gap-2 text-xs text-muted-foreground px-1">
                <ShieldCheck className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                <span>
                  All inquiries are handled confidentially. We typically respond within one business day.
                </span>
              </div>
            </div>

            {/* Right: Form */}
            <Card className="p-6 md:p-8 border-border/60 shadow-sm">
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground tracking-tight">
                  Request Walkthrough
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                   Tell us about your organization so we can route your request and prepare a relevant walkthrough. Access requires a formal company contract.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <HoneypotField value={honeypot} onChange={setHoneypot} />

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Doe"
                      maxLength={100}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@company.com"
                      maxLength={255}
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" value={form.company} maxLength={150} required
                      onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Your role</Label>
                    <Input id="role" value={form.role} maxLength={100} required
                      onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Head of Quality" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sites">Sites</Label>
                    <Select value={form.sites} onValueChange={(v) => setForm({ ...form, sites: v })}>
                      <SelectTrigger id="sites"><SelectValue placeholder="Number of sites" /></SelectTrigger>
                      <SelectContent>{SITES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userRange">Anticipated users</Label>
                    <Select value={form.userRange} onValueChange={(v) => setForm({ ...form, userRange: v })}>
                      <SelectTrigger id="userRange"><SelectValue placeholder="User range" /></SelectTrigger>
                      <SelectContent>{USER_RANGES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="intendedUse">Intended use</Label>
                  <Input id="intendedUse" value={form.intendedUse} maxLength={500} required
                    onChange={(e) => setForm({ ...form, intendedUse: e.target.value })}
                    placeholder="e.g. Document control, deviations and CAPA for clinical supply" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Route to department</Label>
                  <Select
                    value={form.department}
                    onValueChange={(value) => setForm({ ...form, department: value })}
                  >
                    <SelectTrigger id="department">
                      <SelectValue placeholder="Select a department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Anything else we should know</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                     placeholder="Tell us about your scope and current systems. Would you like a private walkthrough, a custom proposal, or GxP validation package and IQ/OQ/PQ support details? Formal validation is performed per customer intended use."
                    rows={7}
                    maxLength={2000}
                    required
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {form.message.length} / 2000
                  </p>
                </div>

                <TurnstileWidget
                  action="contact"
                  onVerify={setCaptchaToken}
                  onExpire={() => setCaptchaToken(null)}
                />

                <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Request Walkthrough
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
